import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import ChartWidgetLoading from "./ChartWidgetLoading";
import ChartWidget from './ChartWidget';
import ObjectUtils from './../../../../../shared/utils/ObjectUtils';
import DateTimeUtils, {TimeZoneUtils as TimezoneUtils} from "../../../../../shared/utils/DateTimeUtils";
import WidgetUtils from "../../../../utils/WidgetUtils";
import DurationFormatter from "../../../../../shared/utils/DurationFormatter";

class ChartWidgetContainer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            windowRange: null
        }
    }

    componentDidMount() {
        const {
            onWidgetDidMount = () => { }
        } = this.props;
        onWidgetDidMount();
    }

    componentDidCatch(error, errorInfo) {
        console.error(error, errorInfo)
    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        if(ObjectUtils.isNil(this.props.data)) {
            return false;
        } else if(nextProps.onError) {
            return true;
        } else if(!this.props.data.loading && nextProps.data.loading) {
            return true;
        }
        return this.props.data.loading && !nextProps.data.loading;
    }

    render() {
        const {
            id = "",
            configs = {},
            data: {
                data = [],
                error: dataError,
                loading: loadingData
            } = {},
            isEditMode,
            isDashboardFullScreen,
            onForceUpdateClick = () => { },
            onEditClick = () => { },
            onDuplicateClicked = () => { },
            onDeleteClicked = () => { }
        } = this.props;

        if (!loadingData || !ObjectUtils.isNilOrEmpty(data)) {
            let chartSeriesConfig = JSON.parse(configs.chartSeriesSelector);
            let options = getChartBaseOptions(chartSeriesConfig, configs.widgetDisplayName, !loadingData);
            options = updateChart(options, chartSeriesConfig, data);

            return (
                <ChartWidget
                    error={dataError}
                    widgetId={id}
                    loading={loadingData}
                    chartOptions={options}
                    widgetDisplayName={configs.widgetDisplayName}
                    isEditMode={isEditMode}
                    isDashboardFullScreen={isDashboardFullScreen}
                    onForceUpdateClick={onForceUpdateClick}
                    onEditClick={onEditClick}
                    onDuplicateClicked={onDuplicateClicked}
                    onDeleteClicked={onDeleteClicked}
                />
            );
        }

        return (
            <ChartWidgetLoading />
        );
    }
}

function getChartBaseOptions(chartSeries, chartName, animation) {
    let now = moment().unix() * 1000;

    let {
        type = 'line'
    } = chartSeries;

    if(type === "step") {
        type = 'line';
    }

    let chartOptions = {
        plotOptions: {
            line: {
                animation: animation
            },
            series: { //This will align two x-axis ticks!!
                pointPlacement: 'on',
                pointInterval: 24 * 3600 * 1000, // one day
            }
        },
        noData: {
            useHTML: false
        },
        chart: {
            type: type,
            zoomType: 'x',
            alignTicks: true,
            height: 330,
            spacingBottom: 0,
            spacingTop: 20,
            spacingLeft: 2,
            spacingRight: 2,
            resetZoomButton: {
                relativeTo: "chart",
                position: {
                    x: -5,
                    y: 0
                },
                theme: {
                    fill: 'white',
                    stroke: 'silver',
                    r: 0,
                    states: {
                        hover: {
                            fill: '#f3f3f4',
                            style: {
                                color: 'black'
                            }
                        }
                    }
                }
            },
            events: {
                beforePrint: function () {
                    this.setTitle({
                        text: chartName
                    })
                },
                afterPrint: function () {
                    this.setTitle({
                        text: null
                    })
                },
                selection: function (event) {
                }
            }
        },
        exporting: {
            chartOptions: {
                title: {
                    text: chartName
                }
            },
            buttons: {
                contextButton: {
                    verticalAlign: 'bottom',
                    menuItems: [
                        'printChart',
                        'downloadPNG',
                        'downloadSVG',
                        'downloadPDF',
                        'downloadCSV',
                        'downloadXLS',
                    ]
                }
            },
            filename: chartName
        },
        tooltip: {
            shared: true,
            formatter: function () {
                let date = this.points[0].series.userOptions.dateTime ?
                    DateTimeUtils.getDateTimeFromUnix(this.points[0].key,  true) :
                    DateTimeUtils.getDateFromUnix(this.points[0].key,  true);

                let tooltipMarkup = this.points ? '<span style="font-size: 10px">' + date + '</span><br/>' : '';

                this.points.forEach(item => {
                    if(item.series.userOptions.seriesType === "time") {
                        const value = parseTimeSeries(item.y, item.series.userOptions.unit);
                        tooltipMarkup += '<br/><span style="color:' + item.series.color + '">\u25CF</span> ' + item.series.name + ': <b>' + value;

                    } else {
                        const unit = !ObjectUtils.isNil(item.series.userOptions.unitSymbol) ? item.series.userOptions.unitSymbol : "";
                        tooltipMarkup += '<br/><span style="color:' + item.series.color + '">\u25CF</span> ' + item.series.name + ': <b>' + item.y.toFixed(2) + unit;
                    }
                });

                return tooltipMarkup;
            }
        },
        title: {
            text: ''
        },
        credits: false
    };

    let avoidConversion = false;
    try {
        avoidConversion = [WidgetUtils.ChartResolution.ONE_DAY,
            WidgetUtils.ChartResolution.ONE_WEEK,
            WidgetUtils.ChartResolution.ONE_MONTH,
            WidgetUtils.ChartResolution.ONE_YEAR].includes(chartSeries.series[0].lines[0].resolution);
    } catch (e) {
        avoidConversion = true;
    }

    if(avoidConversion) {
        chartOptions = {
            ...chartOptions,
            time:  {
                useUTC: true,
            }
        }
    } else {
        chartOptions = {
            ...chartOptions,
            time:  {
                timezoneOffset: TimezoneUtils.getUserTimezoneOffsetInSeconds() * -1 //offset is inversely defined in highcharts
            }
        }
    }

    let xAxis = [];
    let axisConfig = [];
    let mergeYaxis = chartSeries.mergeYaxis;
    let forceZero = chartSeries.forceZero;

    chartSeries.series.forEach( (series, idx) => {
        let xRange = getSeriesRange(series, now);

        // # of X axis
        xAxis.push({
            type: 'datetime',
            min: xRange.start,
            max: xRange.end
        });

        series.lines.forEach(line => {
            axisConfig.push({
                symbol: line.unitSymbol,
                unit: line.unit,
                isBooleanSeries: isBooleanSeries(line.resolutionAggregator)
            });
        });
    });

    chartOptions.xAxis = (xAxis.length > 1) ? xAxis : { type: 'datetime' };

    axisConfig = ObjectUtils.uniq(axisConfig).sort().filter(u => u);

    if (axisConfig.length === 1 || mergeYaxis) {
        const {
            symbol,
            unit,
            isBooleanSeries
        } = axisConfig[0];

        const labelUnit =  mergeYaxis ? "" : symbol;

        chartOptions.yAxis = {
            title: '',
            labels: {
                format: "{value}" + (!ObjectUtils.isNilOrEmpty(labelUnit) ? labelUnit : "")
            }

        };

        if(isBooleanSeries) {
            chartOptions.yAxis = {
                ...chartOptions.yAxis,
                min: 0,
                max: 1,
                startOnTick: false,
                endOnTick: false,
                allowDecimals: false
            }
        }

        if(WidgetUtils.TemporalUnits.includes(unit)) {
            delete chartOptions.yAxis.labels;
            chartOptions.yAxis.labels = {
                formatter: function () {
                    let time = this.value;
                    return parseTimeSeries(time, unit);
                }
            }
        }

        if (forceZero) {
            chartOptions.yAxis.softMin = 0;
            chartOptions.yAxis.minRange = 1;
        }

    } else if (axisConfig.length > 1) {

        chartOptions.yAxis = [];
        axisConfig.forEach(config => {
            let yAxis = {
                title: '',
                labels: {
                    format: "{value}" + (!ObjectUtils.isNilOrEmpty(config.symbol) ? config.symbol : "")
                }
            };

            if(config.isBooleanSeries) {
                yAxis = {
                    ...yAxis,
                    min: 0,
                    max: 1,
                    startOnTick: false,
                    endOnTick: false,
                    allowDecimals: false
                }
            }

            if(WidgetUtils.TemporalUnits.includes(config.unit)) {
                delete yAxis["labels"];
                yAxis.labels = {
                    formatter: function () {
                        let time = this.value;
                        return parseTimeSeries(time, config.unit);
                    }
                }
            }

            if (forceZero) {
                yAxis.softMin = 0;
                yAxis.minRange = 1;
            }

            chartOptions.yAxis.push(yAxis);
        })
    }

    return chartOptions;
}

function updateChart(options, chartSeriesConfig, readings) {
    let seriesObj;
    if (Array.isArray(readings) && !ObjectUtils.isEmpty(readings)) {
        seriesObj = [];

        //Build for each chart series
        let yAxisUnits = chartSeriesConfig.mergeYaxis ? [] : getChartSeriesUnitsOrdered(chartSeriesConfig);
        let colorIdx = 0;

        chartSeriesConfig.series.forEach((serie, seriesIdx) => {

            serie.lines.forEach((line, lineIdx) => {

                let dataSeries = readings.filter(d => {
                    return d.seriesId === seriesIdx && d.lineId === lineIdx;
                });

                let datasetId = seriesIdx + "_" + lineIdx;
                const isBoolean = isBooleanSeries(line.resolutionAggregator);
                let dataset = getNewDatasetForInterface(datasetId, seriesIdx, serie.type, line, colorIdx++, isBoolean, yAxisUnits);

                if(!ObjectUtils.isNilOrEmpty(dataSeries)) {
                    dataSeries[0].readings.forEach(r => {
                        let value = !ObjectUtils.isNilOrEmpty(r[line.resolutionAggregator.toLowerCase()]) ? r[line.resolutionAggregator.toLowerCase()] : r.value;
                        if(isBoolean) {
                            value = value ? 1 : 0;
                        }
                        dataset.data.push([r.timestamp, value]);
                    });
                }

                if(line.cumulativeSeries) {
                    let cumulativeData = [[0, 0]];
                    dataset.data.forEach( (elementToAdd, index) => {
                        cumulativeData.push([elementToAdd[0], cumulativeData[index][1] + elementToAdd[1]]);
                    });
                    cumulativeData.shift();
                    let cumulativeDataset = {
                        ...ObjectUtils.cloneObject(dataset),
                        data: cumulativeData,
                        id: dataset.id + "-cumulative",
                        type: 'line',
                        color: getColor(colorIdx++),
                        lineWidth: 3,
                        name: dataset.name + "-Cumulative",
                        ifaceId: dataset.ifaceId + "-cumulative"
                    };
                    seriesObj.push(cumulativeDataset);
                }

                seriesObj.push(dataset);
            });
        });
    }

    return {
        ...options,
        series : seriesObj
    };
}

function getChartSeriesUnitsOrdered(chartSeriesConfig) {
    let units = [];
    chartSeriesConfig.series.forEach(series => {
        series.lines.forEach(line => {
            units.push({
                symbol: line.unitSymbol,
                isBoolean: isBooleanSeries(line.resolutionAggregator)
            });
        });
    });
    return ObjectUtils.uniq(units).sort();
}

function getNewDatasetForInterface(ifaceId, seriesIdx, type, line, colorIdx, isBooleanSeries, yAxisUnits) {
    const {
        legend,
        unit,
        unitSymbol,
        resolution
    } = line;

    let yAxis = 0;
    yAxisUnits.forEach((u, idx) => {
        if(u.symbol === unitSymbol && u.isBoolean === isBooleanSeries) {
            yAxis = idx;
        }
    });

    let isStep = false;

    if(type === "step") {
        type = 'line';
        isStep = true;
    }

    return {
        id: ifaceId,
        type: type,
        color: getColor(colorIdx),
        lineWidth: 3,
        step: isStep,
        name: legend,
        ifaceId: ifaceId,
        marker: {
            enabled: true,
            radius: 3
        },
        shadow: false,
        data: [],
        dateTime: [WidgetUtils.ChartResolution.FIFTEEN_MIN,
            WidgetUtils.ChartResolution.FIVE_MIN,
            WidgetUtils.ChartResolution.ONE_HOUR].includes(resolution) || isBooleanSeries,
        date: [WidgetUtils.ChartResolution.ONE_DAY,
            WidgetUtils.ChartResolution.ONE_WEEK,
            WidgetUtils.ChartResolution.ONE_MONTH,
            WidgetUtils.ChartResolution.ONE_YEAR].includes(resolution) && !isBooleanSeries,
        seriesType: WidgetUtils.TemporalUnits.includes(unit) ? 'time' : '',
        unitSymbol: unitSymbol,
        unit: unit,
        yAxis: yAxis,
        xAxis: seriesIdx
    };
}

function getColor(colorSeed) {
    let colors = [
        "#5c9bd4",
        "#79c469",
        "#737373",
        "#faa759",
        "#21916d",
        "#f0585f",
        "#6fa6ad",
        "#bb677f",
        "#d77eb4",
        "#9e66ab",
        "#cd6f57",
    ];

    if (colorSeed < colors.length)
        return colors[colorSeed];
    else
        return null;
}

function getSeriesRange(series, end) {
    let seriesRange = {};
    seriesRange.start = moment().subtract(series.windowRange).unix() * 1000;
    seriesRange.end = end ? end : moment().unix() * 1000;
    return seriesRange;
}

function parseTimeSeries(duration, unit) {
    if(unit === WidgetUtils.InterfaceUnits.MINUTES) {
        duration = duration*1000*60;
    } else if (unit === WidgetUtils.InterfaceUnits.SECONDS) {
        duration = duration*1000;
    }
    return DurationFormatter.format(duration, true);
}

function isBooleanSeries(aggregator) {
    return ["ALL_VALUES"].includes(aggregator);
}

ChartWidgetContainer.propTypes = {
    configs: PropTypes.object,
    readings: PropTypes.array,
    isEditMode: PropTypes.bool,
    isDashboardFullScreen: PropTypes.bool,
    onRefreshClicked: PropTypes.func,
    onForceUpdateClick: PropTypes.func,
    onEditClick: PropTypes.func,
    onDuplicateClicked: PropTypes.func,
    getChartData: PropTypes.func
};

export default ChartWidgetContainer;